import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { adminApi, fileUrl } from "../../lib/api";
import { useDialog } from "../../lib/dialog";
import { useSettings } from "../../lib/settings";
import { useEvents } from "../../lib/events";
import AdminLayout from "../../components/app/AdminLayout";
import { Loader } from "./Dashboard";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, Search, Check, Lock, Users, User, Clock, MapPin, LogIn, LayoutGrid, List, ClipboardList, Gavel, Plus, X, RefreshCw, Copy, ChevronDown, ChevronUp } from "lucide-react";

const GROUP_COLORS = [
  { bg: "#D91616", text: "#fff" }, { bg: "#2563EB", text: "#fff" }, { bg: "#16A34A", text: "#fff" },
  { bg: "#D97706", text: "#fff" }, { bg: "#7C3AED", text: "#fff" }, { bg: "#0891B2", text: "#fff" },
  { bg: "#DB2777", text: "#fff" }, { bg: "#65A30D", text: "#fff" },
];
const groupColor = (g) => GROUP_COLORS[((g || 1) - 1) % GROUP_COLORS.length];

export default function AuditionDay() {
  const { aid } = useParams();
  const navigate = useNavigate();
  const dialog = useDialog();
  const { formatDate } = useSettings();
  const { isArchived } = useEvents();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const [pending, setPending] = useState({});
  const [view, setView] = useState(() => localStorage.getItem("ssm_audition_view") || "grid");
  const timer = useRef(null);

  // Live scoring — judges
  const [scoringOpen, setScoringOpen] = useState(false);
  const [judges, setJudges] = useState(null);
  const [judgeBusy, setJudgeBusy] = useState(false);
  const judgeUrl = (code) => `${window.location.origin}/judge${code ? `?code=${code}` : ""}`;

  const loadJudges = () => adminApi.get(`/admin/auditions/${aid}/judges`).then(({ data }) => setJudges(data));
  const openScoring = async () => {
    setScoringOpen(true);
    if (!judges) { await adminApi.post(`/admin/auditions/${aid}/scoring/init`).then(({ data }) => setJudges(data)); }
  };
  const addJudge = async () => {
    setJudgeBusy(true);
    try { await adminApi.post(`/admin/auditions/${aid}/judges`, { name: "" }); await loadJudges(); }
    finally { setJudgeBusy(false); }
  };
  const revokeJudge = async (jid) => {
    if (!(await dialog.confirm({ title: "Revoke this judge code?", description: "The code will stop working immediately. Their submitted scores are kept.", confirmLabel: "Revoke", destructive: true }))) return;
    await adminApi.post(`/admin/judges/${jid}/revoke`); await loadJudges();
  };
  const extendJudge = async (jid) => { await adminApi.post(`/admin/judges/${jid}/extend`, { hours: 12 }); await loadJudges(); };
  const copyCode = (txt) => { navigator.clipboard?.writeText(txt); };


  const setViewMode = (v) => { setView(v); localStorage.setItem("ssm_audition_view", v); };

  const load = () => adminApi.get(`/admin/auditions/${aid}/day`).then(({ data }) => setData(data));
  useEffect(() => {
    load();
    timer.current = setInterval(load, 12000);
    return () => clearInterval(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aid]);

  const au = data?.audition;
  const closed = au?.closed;
  const locked = closed || isArchived;

  const signIn = async (id) => {
    setPending((p) => ({ ...p, [id]: true }));
    try { await adminApi.post(`/admin/auditions/${aid}/signin/${id}`); await load(); }
    finally { setPending((p) => ({ ...p, [id]: false })); }
  };

  const setGroup = async (id, group) => {
    await adminApi.post(`/admin/auditions/${aid}/group/${id}`, { group: parseInt(group, 10) || 0 });
    await load();
  };

  const closeAudition = async () => {
    if (await dialog.confirm({ title: "Close this audition?", description: "Anyone not signed in will be marked Absent and this board becomes read-only. This cannot be undone.", confirmLabel: "Close Audition", destructive: true })) {
      await adminApi.post(`/admin/auditions/${aid}/close`); await load();
    }
  };

  const filtered = useMemo(() => {
    let list = data?.attendees || [];
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((a) => (a.full_name || "").toLowerCase().includes(q) || (a.first_name || "").toLowerCase().includes(q) || (a.last_name || "").toLowerCase().includes(q));
    if (groupFilter !== "all") list = list.filter((a) => String(a.group) === String(groupFilter));
    // signed-in to the bottom; alphabetical within each section
    return [...list].sort((a, b) => {
      const ai = a.attendance === "attended" ? 1 : 0;
      const bi = b.attendance === "attended" ? 1 : 0;
      if (ai !== bi) return ai - bi;
      return (a.full_name || "").localeCompare(b.full_name || "");
    });
  }, [data, search, groupFilter]);

  if (!data) return <AdminLayout title="Audition"><Loader /></AdminLayout>;

  return (
    <AdminLayout title={au.name} subtitle={`${formatDate ? formatDate(au.date) : au.date} · ${au.start_time}–${au.end_time}`}
      actions={<div className="flex items-center gap-2">
        <button onClick={() => navigate("/admin/auditions")} data-testid="audition-back-btn" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-[#3a3835] hover:text-[#F2EBE1]"><ArrowLeft size={14} /> Back</button>
        <a href="/signin" target="_blank" rel="noopener noreferrer" data-testid="audition-signin-sheet-link" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-blood hover:text-blood"><ClipboardList size={14} /> Sign-In Sheet</a>
        <button onClick={openScoring} data-testid="audition-scoring-btn" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-blood hover:text-blood"><Gavel size={14} /> Judge Scoring</button>
        {!locked && <button onClick={closeAudition} data-testid="audition-close-btn" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]"><Lock size={14} /> Close Audition</button>}
      </div>}>

      {closed && <div className="mb-4 px-4 py-2.5 text-sm border border-[#262626] bg-[#0A0A0A] text-[#A19D94] flex items-center gap-2" data-testid="audition-closed-banner"><Lock size={14} /> This audition is closed and read-only.</div>}

      {/* Stats + meta */}
      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <Stat label="Expected" value={data.expected} testid="audition-expected" />
        <Stat label="Signed In" value={data.signed_in} accent testid="audition-signed-in" />
        <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-4 text-sm text-[#A19D94] flex flex-col justify-center gap-1">
          {au.location && <span className="flex items-center gap-1.5"><MapPin size={13} className="text-blood" /> {au.location}</span>}
          <span className="flex items-center gap-1.5"><Clock size={13} className="text-blood" /> {au.start_time}–{au.end_time}</span>
        </div>
      </div>

      {/* Live Judge Scoring panel */}
      {scoringOpen && (
        <div className="mb-4 border border-[#262626] bg-[#0A0A0A]" data-testid="scoring-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#1a1a1a]">
            <div className="flex items-center gap-2">
              <Gavel size={16} className="text-blood" />
              <h3 className="font-display uppercase tracking-tight">Judge Scoring</h3>
            </div>
            <button onClick={() => setScoringOpen(false)} data-testid="scoring-close-btn" className="text-[#6B6862] hover:text-[#F2EBE1]"><X size={16} /></button>
          </div>
          <div className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <div className="flex flex-col items-center bg-white p-3 rounded shrink-0" data-testid="judge-scoring-qr">
                <QRCodeSVG value={judgeUrl("")} size={132} />
                <span className="text-[10px] text-black mt-2 font-medium">Scan to open judge scoring</span>
              </div>
              <div className="text-sm text-[#A19D94] space-y-2">
                <p>Judges scan this QR (or open <span className="font-mono text-[#F2EBE1]">{judgeUrl("")}</span>) on their phone, then enter one of the access codes below.</p>
                <p className="text-xs text-[#6B6862]">Scores are private to each judge. Up to {judges ? judges.max_temp : 3} temporary judges plus you (master).</p>
              </div>
            </div>

            {!judges ? (
              <div className="py-6 flex justify-center"><Loader /></div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3" data-testid="judge-cards">
                {judges.judges.map((j) => {
                  const active = j.active && !j.revoked;
                  return (
                    <div key={j.id} className={`border p-3 ${active ? "border-[#1a1a1a]" : "border-[#2a1414] opacity-60"}`} data-testid={`judge-card-${j.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-display uppercase tracking-tight truncate">{j.name}</div>
                          <div className="text-[10px] uppercase tracking-wider text-[#6B6862]">{j.kind === "master" ? "Master" : "Temporary"} · {j.scored_count} scored</div>
                        </div>
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${active ? "text-[#22C55E] border-[#16A34A]/40" : "text-blood border-blood/40"}`}>{j.revoked ? "Revoked" : active ? "Active" : "Expired"}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="font-mono text-2xl tracking-widest text-[#F2EBE1]" data-testid={`judge-code-${j.id}`}>{j.code}</span>
                        <button onClick={() => copyCode(j.code)} title="Copy code" data-testid={`judge-copy-${j.id}`} className="text-[#6B6862] hover:text-[#F2EBE1]"><Copy size={14} /></button>
                      </div>
                      {j.kind !== "master" && (
                        <div className="flex gap-2 mt-3">
                          {active ? (
                            <button onClick={() => revokeJudge(j.id)} data-testid={`judge-revoke-${j.id}`} className="flex-1 flex items-center justify-center gap-1 text-[11px] uppercase tracking-wider border border-[#262626] py-1.5 hover:border-blood hover:text-blood"><X size={12} /> Revoke</button>
                          ) : (
                            <button onClick={() => extendJudge(j.id)} data-testid={`judge-extend-${j.id}`} className="flex-1 flex items-center justify-center gap-1 text-[11px] uppercase tracking-wider border border-[#262626] py-1.5 hover:border-[#22C55E] hover:text-[#22C55E]"><RefreshCw size={12} /> Re-activate 12h</button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {judges && (() => {
              const activeTemps = judges.judges.filter((j) => j.kind === "temp" && !j.revoked && j.active).length;
              return activeTemps < judges.max_temp ? (
                <div className="flex items-center gap-3">
                  <button onClick={addJudge} disabled={judgeBusy} data-testid="judge-add-btn"
                    className="flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333] disabled:opacity-40">
                    <Plus size={14} /> Add Judge Code
                  </button>
                  <span className="text-[11px] text-[#6B6862]">{activeTemps}/{judges.max_temp} temp judges active</span>
                </div>
              ) : (
                <p className="text-[11px] text-[#6B6862]" data-testid="judge-cap-hint">All {judges.max_temp} temporary judge slots are in use. Revoke one to free a slot.</p>
              );
            })()}
          </div>
        </div>
      )}

      {/* Group summary bar */}
      {au.groups_enabled && data.summary?.length > 0 && (        <div className="flex flex-wrap gap-2 mb-4" data-testid="audition-group-summary">
          {data.summary.map((s) => {
            const c = groupColor(s.group);
            return (
              <div key={s.group} className="flex items-center gap-2 px-3 py-2 border border-[#1a1a1a] bg-[#0A0A0A]">
                <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold" style={{ background: c.bg, color: c.text }}>{s.group}</span>
                <span className="text-sm"><span className="text-[#F2EBE1] font-mono">{s.signed_in}/{s.total}</span> <span className="text-[#6B6862] text-xs">signed in</span></span>
              </div>
            );
          })}
        </div>
      )}

      {/* Search + group filter */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6862]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name…" data-testid="audition-search"
            className="w-full bg-[#050505] border border-[#262626] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
        </div>
        {au.groups_enabled && (
          <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} data-testid="audition-group-filter"
            className="bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood">
            <option value="all">All groups</option>
            {Array.from({ length: au.group_count || 0 }, (_, i) => i + 1).map((g) => <option key={g} value={g}>Group {g}</option>)}
          </select>
        )}
        <div className="flex border border-[#262626]" data-testid="audition-view-toggle">
          <button onClick={() => setViewMode("grid")} data-testid="audition-view-grid" title="Grid view"
            className={`px-3 py-2.5 ${view === "grid" ? "bg-blood text-white" : "text-[#6B6862] hover:text-[#F2EBE1]"}`}><LayoutGrid size={15} /></button>
          <button onClick={() => setViewMode("list")} data-testid="audition-view-list" title="List view"
            className={`px-3 py-2.5 border-l border-[#262626] ${view === "list" ? "bg-blood text-white" : "text-[#6B6862] hover:text-[#F2EBE1]"}`}><List size={15} /></button>
        </div>
      </div>

      {/* Attendee cards */}
      {view === "grid" && (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.length === 0 && <p className="col-span-full text-sm text-[#6B6862] py-8 text-center">No attendees match.</p>}
        {filtered.map((a) => {
          const signed = a.attendance === "attended";
          const c = a.group ? groupColor(a.group) : null;
          return (
            <div key={a.id} data-testid={`audition-attendee-${a.id}`}
              className={`border p-4 transition-colors ${signed ? "border-[#10B981] bg-[#10B981]/5" : "border-[#1a1a1a] bg-[#0A0A0A]"}`}>
              <div className="flex gap-3">
                <div className="w-16 h-16 shrink-0 border border-[#262626] bg-[#050505] overflow-hidden flex items-center justify-center">
                  {a.headshot_file_id ? <img src={fileUrl(a.headshot_file_id, { thumb: true })} alt={a.full_name} loading="lazy" decoding="async" className="w-full h-full object-cover" /> : <User size={24} className="text-[#3a3835]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base uppercase tracking-tight leading-tight truncate">{a.full_name}</div>
                  <div className="text-[11px] text-[#6B6862] mt-0.5 space-x-2">
                    {a.age && <span>{a.age} yrs</span>}
                    {a.gender && <span>· {a.gender}</span>}
                  </div>
                  {a.experience_level && <div className="text-[11px] text-[#A19D94] mt-0.5 truncate">{a.experience_level}</div>}
                  {au.groups_enabled && (
                    locked ? (
                      a.group ? <span className="inline-flex items-center justify-center mt-1.5 px-2 py-0.5 text-[10px] font-bold" style={{ background: c.bg, color: c.text }}>Group {a.group}</span> : null
                    ) : (
                      <select value={a.group || ""} onChange={(e) => setGroup(a.id, e.target.value)} data-testid={`audition-group-select-${a.id}`}
                        className="mt-1.5 text-[11px] bg-[#050505] border border-[#262626] px-1.5 py-0.5 focus:outline-none focus:border-blood"
                        style={a.group ? { borderColor: c.bg } : {}}>
                        <option value="">No group</option>
                        {Array.from({ length: au.group_count || 0 }, (_, i) => i + 1).map((g) => <option key={g} value={g}>Group {g}</option>)}
                      </select>
                    )
                  )}
                </div>
              </div>
              <div className="mt-3">
                {signed ? (
                  <div className="flex items-center justify-center gap-1.5 py-2 text-xs uppercase tracking-wider text-[#10B981] border border-[#10B981]/40" data-testid={`audition-signed-${a.id}`}>
                    <Check size={14} /> Signed in {a.signed_in_at ? `· ${a.signed_in_at.slice(11, 16)}` : ""}
                  </div>
                ) : (
                  <button onClick={() => signIn(a.id)} disabled={locked || pending[a.id]} data-testid={`audition-signin-${a.id}`}
                    className="w-full flex items-center justify-center gap-1.5 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333] disabled:opacity-40">
                    <LogIn size={14} /> Sign In
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* Attendee list view */}
      {view === "list" && (
      <div className="border border-[#1a1a1a] bg-[#0A0A0A] divide-y divide-[#141414]" data-testid="audition-list">
        {filtered.length === 0 && <p className="text-sm text-[#6B6862] py-8 text-center">No attendees match.</p>}
        {filtered.map((a) => {
          const signed = a.attendance === "attended";
          const c = a.group ? groupColor(a.group) : null;
          return (
            <div key={a.id} data-testid={`audition-attendee-row-${a.id}`}
              className={`flex items-center gap-3 px-3 py-2 ${signed ? "bg-[#10B981]/5" : ""}`}>
              <div className="w-10 h-10 shrink-0 rounded-full border border-[#262626] bg-[#050505] overflow-hidden flex items-center justify-center">
                {a.headshot_file_id ? <img src={fileUrl(a.headshot_file_id, { thumb: true })} alt={a.full_name} loading="lazy" decoding="async" className="w-full h-full object-cover" /> : <User size={18} className="text-[#3a3835]" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-sm uppercase tracking-tight leading-tight truncate">{a.full_name}</div>
                <div className="text-[10px] text-[#6B6862] space-x-2">
                  {a.age && <span>{a.age} yrs</span>}
                  {a.gender && <span>· {a.gender}</span>}
                </div>
              </div>
              {au.groups_enabled && (
                locked ? (
                  a.group ? <span className="shrink-0 inline-flex items-center justify-center px-2 py-0.5 text-[10px] font-bold" style={{ background: c.bg, color: c.text }} data-testid={`audition-row-group-${a.id}`}>G{a.group}</span> : null
                ) : (
                  <select value={a.group || ""} onChange={(e) => setGroup(a.id, e.target.value)} data-testid={`audition-row-group-select-${a.id}`}
                    className="shrink-0 text-[11px] font-bold bg-[#050505] border px-1.5 py-1 focus:outline-none focus:border-blood cursor-pointer"
                    style={a.group ? { borderColor: c.bg, color: c.text, background: c.bg } : { borderColor: "#262626" }}>
                    <option value="" style={{ color: "#F2EBE1", background: "#050505" }}>No grp</option>
                    {Array.from({ length: au.group_count || 0 }, (_, i) => i + 1).map((g) => <option key={g} value={g} style={{ color: "#F2EBE1", background: "#050505" }}>Group {g}</option>)}
                  </select>
                )
              )}
              <div className="shrink-0">
                {signed ? (
                  <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-[#10B981]" data-testid={`audition-row-signed-${a.id}`}>
                    <Check size={14} /> In{a.signed_in_at ? ` ${a.signed_in_at.slice(11, 16)}` : ""}
                  </span>
                ) : (
                  <button onClick={() => signIn(a.id)} disabled={locked || pending[a.id]} data-testid={`audition-row-signin-${a.id}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333] disabled:opacity-40">
                    <LogIn size={13} /> Sign In
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      )}
    </AdminLayout>
  );
}

const Stat = ({ label, value, accent, testid }) => (
  <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-4" data-testid={testid}>
    <div className="label-mini">{label}</div>
    <div className={`font-display text-4xl mt-1 ${accent ? "text-[#10B981]" : "text-[#F2EBE1]"}`}>{value}</div>
  </div>
);
